// PRD 16 C2 — the data-collection ledger, at /trust.
//
// NOT AN ANALYTICS VIEW. This is the page that deletes the security review
// which otherwise kills a third-party script on a production domain. Its
// reader is a security engineer or a program office filling in a vendor
// questionnaire, and they read it adversarially. Everything it claims is
// already true and already tested; unmarketed, it was invisible.
//
// SEPARATE SURFACE, per PRD 16 decision D2. It is a root route, not a child
// of `_shell`, and takes no org: it renders a build-time constant, so there
// is no tenant data on it to gate, and D2's stated consequence is that a
// reviewer can be shown this BEFORE purchase, without an account on the main
// surface. Gating a page that reads nothing would be theatre; if the product
// later wants it behind the shell too, the screen mounts anywhere.
//
// The claims live in `ledger.ts`, pinned to `backend/analytics/schema.sql`
// AND to the widget's real emit call sites by `schemaPin.test.ts`. This file
// paints them and adds no facts of its own.
//
// Every row carries a sent/declared tag, because some declared event types
// and columns have no producer anywhere in the tree. The reasoning for
// showing them rather than dropping them is in `ledger.ts`'s header; the
// short version is that a reviewer reads `types.py` too, and a name that
// appears there but not here reads as concealment. The counts are never
// written into the copy — they are derived, so the page cannot drift out of
// step with the ledger the way its first draft did.
//
// COUNSEL REVIEW PENDING (docs/business/05 §5.2) — the user-facing copy on
// this screen has not been reviewed by counsel. It is deliberately free of
// compliance, certification and regulatory language; keep it that way until
// that review happens, and never write "we anonymize it" (CLAUDE.md
// §Non-goals — a hashed identifier is pseudonymous, not anonymous).

import "../../tokens/paper.css";
import { Badge, Card, PageHeader, Table } from "../../ui";
import {
  COLLECTED_FIELDS,
  CONSEQUENCES,
  EVENT_TYPES,
  NOT_COLLECTED,
  PROCESSING,
  type Claim,
  type EmissionStatus,
} from "./ledger";
import "./trust.css";

const SENT_COUNT = EVENT_TYPES.filter((event) => event.status === "sent").length;

// The two-layer disclosure, in one sentence each. A row marked "declared" is
// a column or an event name a reviewer will find in the schema and the enum;
// saying outright that nothing produces it is what makes the rest checkable.
function StatusTag({ status }: { status: EmissionStatus }) {
  return status === "sent" ? (
    <Badge tone="nominal">sent</Badge>
  ) : (
    <Badge tone="neutral">declared, not sent</Badge>
  );
}

export function DataLedgerScreen() {
  return (
    <div className="maisight-trust-page" data-chassis="paper">
      <main className="maisight-trust-body">
        <PageHeader
          eyebrow="Data collection"
          title="Exactly what mAiSight records"
          description="Rendered from the event schema this deployment runs, not from a policy document. The table below is the whole of what a reader can produce, and it distinguishes what our code actually sends from what the schema merely allows. A column or an event that stops matching this page fails our build."
        />

        <Card header="Every field the event record can hold">
          <p className="maisight-trust-lede type-bodyS">
            These are all the columns the event table has, which is the ceiling
            on what could be stored without a code change. Any column marked{" "}
            <strong>declared, not sent</strong> exists in the schema and nothing
            we ship ever fills it, so every row is empty.
          </p>
          <Table
            label="Fields stored per reader event"
            columns={[
              {
                key: "column",
                header: "Field",
                cell: (field) => (
                  <span className="maisight-trust-field type-numSm">
                    {field.column}
                  </span>
                ),
              },
              { key: "type", header: "Type", cell: (field) => field.type },
              {
                key: "status",
                header: "Status",
                cell: (field) => <StatusTag status={field.status} />,
              },
              {
                key: "description",
                header: "What it is",
                cell: (field) => field.description,
              },
            ]}
            rows={COLLECTED_FIELDS}
            rowKey={(field) => field.column}
            maxRows={COLLECTED_FIELDS.length}
            unit="fields"
          />
        </Card>

        <Card header="The events themselves">
          <p className="maisight-trust-lede type-bodyS">
            Our event enum declares {EVENT_TYPES.length}. Only {SENT_COUNT} of
            them are produced by anything we ship — the rest are names in a
            type declaration with no code behind them, and no row of that kind
            has ever been recorded.
          </p>
          <ul className="maisight-trust-events">
            {EVENT_TYPES.map((event) => (
              <li key={event.name} className="maisight-trust-event">
                <span className="maisight-trust-field type-numSm">
                  {event.name}
                </span>
                <StatusTag status={event.status} />
                <span className="type-bodyS">{event.description}</span>
              </li>
            ))}
          </ul>
        </Card>

        <ClaimCard
          header="What we don't collect"
          lede="Stated as non-collection, because that is what the code does. Each line names where it can be checked."
          claims={NOT_COLLECTED}
        />

        <ClaimCard
          header="What follows from that"
          lede="These are properties of the design, not features we have yet to build. They cannot be turned on."
          claims={CONSEQUENCES}
        />

        <ClaimCard
          header="Where your document text goes"
          lede="Reader behaviour is one question; your content is the other. This is the whole of it."
          claims={PROCESSING}
        />
      </main>
    </div>
  );
}

function ClaimCard({
  header,
  lede,
  claims,
}: {
  header: string;
  lede: string;
  claims: Claim[];
}) {
  return (
    <Card header={header}>
      <p className="maisight-trust-lede type-bodyS">{lede}</p>
      <ul className="maisight-trust-claims">
        {claims.map((entry) => (
          <li key={entry.claim} className="maisight-trust-claim">
            <p className="maisight-trust-claim-text type-body">{entry.claim}</p>
            <p className="maisight-trust-source type-bodyS">{entry.source}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
